import { useState, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebaseConfig";
import { useAuth } from "../../contexts/AuthContext";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FiUsers,
  FiHome,
  FiKey,
  FiRefreshCw,
  FiPause,
  FiPlay,
  FiCopy,
  FiCheck,
  FiArrowLeft,
  FiShield,
} from "react-icons/fi";

const AdminDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("tenants");
  const [copiedKey, setCopiedKey] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const getAllTenants = httpsCallable(functions, "getAllTenants");
      const getAllUsers = httpsCallable(functions, "getAllUsers");

      const [tenantsResult, usersResult] = await Promise.all([
        getAllTenants(),
        getAllUsers(),
      ]);

      setTenants(tenantsResult.data);
      setUsers(usersResult.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (tenantId) => {
    setActionLoading(tenantId);
    try {
      const toggleTenantStatus = httpsCallable(functions, "toggleTenantStatus");
      await toggleTenantStatus({ tenantId });
      await fetchData();
    } catch (error) {
      console.error("Error toggling status:", error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRegenerateKey = async (tenantId) => {
    setActionLoading(`key-${tenantId}`);
    try {
      const regenerateApiKey = httpsCallable(functions, "regenerateApiKey");
      await regenerateApiKey({ tenantId });
      await fetchData();
    } catch (error) {
      console.error("Error regenerating key:", error);
    } finally {
      setActionLoading(null);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="text-blue-400 text-4xl"
        >
          <FiRefreshCw />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white p-4 md:p-8">
      {/* Header */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="mb-8"
      >
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <FiShield className="text-3xl text-red-400" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                System Admin
              </h1>
              <p className="text-gray-400 text-sm">NexusFlow Management</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              <FiArrowLeft />
              Dashboard
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </motion.header>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800/50 border border-gray-700 rounded-xl p-6"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <FiHome className="text-blue-400 text-xl" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total Tenants</p>
              <p className="text-2xl font-bold">{tenants.length}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gray-800/50 border border-gray-700 rounded-xl p-6"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-500/20 rounded-lg">
              <FiUsers className="text-green-400 text-xl" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total Users</p>
              <p className="text-2xl font-bold">{users.length}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gray-800/50 border border-gray-700 rounded-xl p-6"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-yellow-500/20 rounded-lg">
              <FiPause className="text-yellow-400 text-xl" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Suspended</p>
              <p className="text-2xl font-bold">
                {tenants.filter((t) => t.status === "suspended").length}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab("tenants")}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === "tenants"
              ? "bg-blue-600 text-white"
              : "bg-gray-700 text-gray-300 hover:bg-gray-600"
          }`}
        >
          <FiHome className="inline mr-2" />
          Tenants
        </button>
        <button
          onClick={() => setActiveTab("users")}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === "users"
              ? "bg-blue-600 text-white"
              : "bg-gray-700 text-gray-300 hover:bg-gray-600"
          }`}
        >
          <FiUsers className="inline mr-2" />
          Users
        </button>
      </div>

      {/* Tenants Table */}
      {activeTab === "tenants" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800">
                <tr>
                  <th className="text-left p-4 text-gray-400">Company</th>
                  <th className="text-left p-4 text-gray-400">Status</th>
                  <th className="text-left p-4 text-gray-400">Users</th>
                  <th className="text-left p-4 text-gray-400">API Key</th>
                  <th className="text-left p-4 text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((tenant) => (
                  <tr
                    key={tenant.id}
                    className="border-t border-gray-700 hover:bg-gray-700/30"
                  >
                    <td className="p-4">
                      <div>
                        <p className="font-medium">{tenant.name}</p>
                        <p className="text-gray-500 text-sm">{tenant.slug}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          tenant.status === "active"
                            ? "bg-green-500/20 text-green-400"
                            : "bg-red-500/20 text-red-400"
                        }`}
                      >
                        {tenant.status}
                      </span>
                    </td>
                    <td className="p-4">{tenant.userCount}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <code className="bg-gray-900 px-2 py-1 rounded text-xs text-gray-400 max-w-[150px] truncate">
                          {tenant.apiKey}
                        </code>
                        <button
                          onClick={() =>
                            copyToClipboard(tenant.apiKey, tenant.id)
                          }
                          className="p-1 hover:bg-gray-600 rounded"
                        >
                          {copiedKey === tenant.id ? (
                            <FiCheck className="text-green-400" />
                          ) : (
                            <FiCopy className="text-gray-400" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleStatus(tenant.id)}
                          disabled={actionLoading === tenant.id}
                          className={`p-2 rounded-lg transition-colors ${
                            tenant.status === "active"
                              ? "bg-red-500/20 hover:bg-red-500/30 text-red-400"
                              : "bg-green-500/20 hover:bg-green-500/30 text-green-400"
                          }`}
                          title={
                            tenant.status === "active" ? "Suspend" : "Activate"
                          }
                        >
                          {actionLoading === tenant.id ? (
                            <FiRefreshCw className="animate-spin" />
                          ) : tenant.status === "active" ? (
                            <FiPause />
                          ) : (
                            <FiPlay />
                          )}
                        </button>
                        <button
                          onClick={() => handleRegenerateKey(tenant.id)}
                          disabled={actionLoading === `key-${tenant.id}`}
                          className="p-2 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded-lg transition-colors"
                          title="Regenerate API Key"
                        >
                          {actionLoading === `key-${tenant.id}` ? (
                            <FiRefreshCw className="animate-spin" />
                          ) : (
                            <FiKey />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {tenants.length === 0 && (
                  <tr>
                    <td colSpan="5" className="p-8 text-center text-gray-500">
                      No tenants found. Register a company to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Users Table */}
      {activeTab === "users" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800">
                <tr>
                  <th className="text-left p-4 text-gray-400">User</th>
                  <th className="text-left p-4 text-gray-400">Company</th>
                  <th className="text-left p-4 text-gray-400">Role</th>
                  <th className="text-left p-4 text-gray-400">Status</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="border-t border-gray-700 hover:bg-gray-700/30"
                  >
                    <td className="p-4">
                      <div>
                        <p className="font-medium">{user.displayName}</p>
                        <p className="text-gray-500 text-sm">{user.email}</p>
                      </div>
                    </td>
                    <td className="p-4">{user.tenantName}</td>
                    <td className="p-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          user.role === "owner"
                            ? "bg-purple-500/20 text-purple-400"
                            : user.role === "admin"
                            ? "bg-blue-500/20 text-blue-400"
                            : "bg-gray-500/20 text-gray-400"
                        }`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          user.status === "active"
                            ? "bg-green-500/20 text-green-400"
                            : "bg-red-500/20 text-red-400"
                        }`}
                      >
                        {user.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan="4" className="p-8 text-center text-gray-500">
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AdminDashboard;